import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  PaperPlaneTilt,
  Eye,
  Flask,
  Users,
  ClockCounterClockwise,
  Sparkle,
  Robot,
  Copy,
  TestTube,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNewsletter = () => {
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmBlast, setConfirmBlast] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  const loadAll = async () => {
    try {
      const [p, r, h, d] = await Promise.all([
        axios.get(`${API}/admin/newsletter/preview`),
        axios.get(`${API}/admin/newsletter/recipients`),
        axios.get(`${API}/admin/newsletter/history?limit=10`),
        axios.get(`${API}/admin/newsletter/draft`),
      ]);
      setPreview(p.data);
      const recs = r.data?.recipients || [];
      setRecipients(recs);
      // Default exclusion: auto-flag service accts, test accts, duplicates
      const autoExcluded = new Set(
        recs
          .filter((u) => u.is_service_account || u.is_test_account || u.is_duplicate)
          .map((u) => u.user_id)
      );
      setExcludedIds(autoExcluded);
      setHistory(h.data?.sends || []);
      if (d.data?.subject) setSubject(d.data.subject);
      if (d.data?.html_body) setHtmlBody(d.data.html_body);
      if (d.data?.saved_at) setDraftSavedAt(d.data.saved_at);
    } catch (err) {
      toast.error("Failed to load newsletter data");
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Auto-save draft 1.5s after the user stops typing.
  useEffect(() => {
    if (!subject && !htmlBody) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/admin/newsletter/draft`, {
          subject, html_body: htmlBody,
        });
        setDraftSavedAt(res.data?.saved_at || new Date().toISOString());
      } catch { /* silent — draft autosave failure isn't user-facing */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [subject, htmlBody]);

  const selectedCount = useMemo(
    () => recipients.filter((r) => !excludedIds.has(r.user_id)).length,
    [recipients, excludedIds]
  );

  const toggleRecipient = (userId) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => setExcludedIds(new Set());
  const selectNone = () => setExcludedIds(new Set(recipients.map((r) => r.user_id)));
  const autoClean = () => {
    setExcludedIds(
      new Set(
        recipients
          .filter((u) => u.is_service_account || u.is_test_account || u.is_duplicate)
          .map((u) => u.user_id)
      )
    );
    toast.success("Auto-cleaned: service accounts, test accounts, and duplicates excluded");
  };

  const sendTest = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/send`, {
        subject, html_body: htmlBody, test_only: true,
      });
      toast.success(`Test sent to ${res.data.sent_to}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally {
      setSending(false);
    }
  };

  const sendBlast = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setSending(true);
    setConfirmBlast(false);
    try {
      const res = await axios.post(`${API}/admin/newsletter/send`, {
        subject,
        html_body: htmlBody,
        test_only: false,
        excluded_user_ids: Array.from(excludedIds),
      });
      toast.success(`Sent to ${res.data.sent} subscribers (${res.data.failed} failed, ${res.data.excluded || 0} excluded)`);
      setSubject(""); setHtmlBody("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blast send failed");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-newsletter-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Newsletter</h1>
          <p className="text-zinc-400 mt-1">Send the Sunday Grant Drop (or any blast) to subscribers</p>
        </div>

        {/* Recipient picker */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="newsletter-recipients-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Users weight="fill" className="w-4 h-4 text-emerald-400" />
                Recipients
                <span className="text-xs font-normal text-zinc-500 ml-2">
                  <span className="text-emerald-400 font-mono text-base" data-testid="newsletter-selected-count">{selectedCount}</span>
                  <span className="mx-1">/</span>
                  <span className="font-mono">{recipients.length}</span>
                  <span className="ml-2 uppercase tracking-widest">selected</span>
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={autoClean} size="sm" variant="outline" className="bg-zinc-900 border-zinc-700 text-xs h-7" data-testid="newsletter-auto-clean-btn">
                  <Sparkle className="w-3 h-3 mr-1" /> Auto-clean
                </Button>
                <Button onClick={selectAll} size="sm" variant="outline" className="bg-zinc-900 border-zinc-700 text-xs h-7" data-testid="newsletter-select-all-btn">
                  Select all
                </Button>
                <Button onClick={selectNone} size="sm" variant="outline" className="bg-zinc-900 border-zinc-700 text-xs h-7" data-testid="newsletter-select-none-btn">
                  Select none
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recipients.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">No eligible recipients found.</p>
            ) : (
              <div className="max-h-[360px] overflow-y-auto rounded-sm border border-zinc-800 divide-y divide-zinc-900">
                {recipients.map((r) => {
                  const checked = !excludedIds.has(r.user_id);
                  const flags = [];
                  if (r.is_service_account) flags.push({ label: "service", icon: Robot, color: "text-amber-400" });
                  if (r.is_test_account) flags.push({ label: "test", icon: TestTube, color: "text-orange-400" });
                  if (r.is_duplicate) flags.push({ label: "duplicate", icon: Copy, color: "text-rose-400" });
                  return (
                    <label
                      key={r.user_id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-900/60 transition-colors ${checked ? "" : "opacity-50"}`}
                      data-testid={`newsletter-recipient-${r.user_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRecipient(r.user_id)}
                        className="w-4 h-4 accent-emerald-500 cursor-pointer"
                        data-testid={`newsletter-recipient-checkbox-${r.user_id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-zinc-100 truncate">{r.name || "(no name)"}</span>
                          <span className="text-xs text-zinc-500 font-mono truncate">{r.email}</span>
                          {r.subscription_tier && r.subscription_tier !== "free" && (
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                              {r.subscription_tier}
                            </span>
                          )}
                          {r.role === "admin" && (
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-violet-500/10 text-violet-300 border border-violet-500/30">
                              admin
                            </span>
                          )}
                          {flags.map((f, i) => (
                            <span key={i} className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border bg-zinc-900 ${f.color} border-zinc-700 inline-flex items-center gap-1`}>
                              <f.icon className="w-3 h-3" /> {f.label}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-[10px] text-zinc-600 font-mono flex-shrink-0">
                        {formatDate(r.created_at)}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-zinc-500 mt-3">
              <Sparkle className="w-3 h-3 inline mr-1" />
              Service accounts, test accounts, and duplicate emails are auto-excluded on load. Click any row to toggle.
            </p>
          </CardContent>
        </Card>

        {/* Compose */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="newsletter-compose-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Compose</span>
              {draftSavedAt && (
                <span className="text-[10px] font-normal text-zinc-500" data-testid="newsletter-draft-saved">
                  Draft auto-saved · {new Date(draftSavedAt).toLocaleTimeString()}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1">Subject line</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="5 SC grants to know this week — one for founders without a 501(c)(3)"
                className="bg-zinc-900 border-zinc-800"
                data-testid="newsletter-subject-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-widest text-zinc-500">HTML body</label>
                <button
                  type="button"
                  onClick={() => setPreviewMode(!previewMode)}
                  className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                  data-testid="newsletter-preview-toggle"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {previewMode ? "Edit HTML" : "Preview rendered"}
                </button>
              </div>
              {previewMode ? (
                <div
                  className="rounded-sm bg-white text-black p-4 min-h-[400px] max-h-[600px] overflow-y-auto border border-zinc-800"
                  dangerouslySetInnerHTML={{ __html: htmlBody || "<p class='text-zinc-400'>Nothing to preview yet.</p>" }}
                  data-testid="newsletter-preview-rendered"
                />
              ) : (
                <Textarea
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  placeholder="<h1>This week's SC grant drop</h1><p>...</p>"
                  className="bg-zinc-900 border-zinc-800 font-mono text-sm min-h-[400px]"
                  data-testid="newsletter-body-input"
                />
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={sendTest}
                disabled={sending}
                variant="outline"
                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                data-testid="newsletter-send-test-btn"
              >
                <Flask className="w-4 h-4 mr-2" />
                Send test to me
              </Button>
              <Button
                onClick={() => setConfirmBlast(true)}
                disabled={sending || selectedCount === 0}
                className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                data-testid="newsletter-send-blast-btn"
              >
                <PaperPlaneTilt weight="fill" className="w-4 h-4 mr-2" />
                {sending ? "Sending..." : `Send to ${selectedCount} selected`}
              </Button>
              {preview?.will_throttle && selectedCount > 100 && (
                <div className="text-xs text-amber-400 self-center">
                  Above Resend&apos;s 100/day cap — will throttle
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Confirm modal */}
        {confirmBlast && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            data-testid="newsletter-confirm-modal"
          >
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 max-w-md w-full">
              <h3 className="text-lg font-bold mb-2">Send to {selectedCount} subscribers?</h3>
              <p className="text-sm text-zinc-400 mb-2">
                Subject: <span className="text-zinc-200 font-semibold">{subject}</span>
              </p>
              <p className="text-sm text-zinc-500 mb-4">
                {excludedIds.size > 0 && (
                  <>Excluding <span className="text-zinc-300 font-semibold">{excludedIds.size}</span> recipients (service / test / duplicates / manually unchecked).<br /></>
                )}
                There&apos;s no undo.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConfirmBlast(false)} className="bg-zinc-900 border-zinc-700" data-testid="newsletter-confirm-cancel">
                  Cancel
                </Button>
                <Button onClick={sendBlast} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="newsletter-confirm-send">
                  Yes, send it
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        <Card className="bg-zinc-950 border-zinc-800" data-testid="newsletter-history-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClockCounterClockwise className="w-4 h-4" />
              Send history
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">No sends yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.send_id} className="flex items-center justify-between gap-3 p-2 rounded-sm bg-zinc-900/40 border border-zinc-800 text-sm" data-testid={`newsletter-history-${h.send_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-zinc-200 font-semibold truncate">{h.subject}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(h.sent_at).toLocaleString()}
                        {h.test_only && <span className="ml-2 text-amber-400">[TEST]</span>}
                      </p>
                    </div>
                    <div className="text-right text-xs text-zinc-400 flex-shrink-0">
                      <p className="font-mono text-zinc-200">{h.recipients_count}</p>
                      <p className="text-[10px] uppercase tracking-widest">sent</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;
